/**
 * Billing vocabulary shared by the API and the console.
 *
 * Browser-safe on purpose, like everything in this package: the console needs
 * these names to offer the right buttons, and the API needs them to validate a
 * checkout request — one list, so the two cannot drift.
 *
 * Price identifiers are Stripe *lookup keys*, not price ids. The catalog is
 * created once per Stripe mode (sandbox for dev, live for prod) with these
 * keys, and the API resolves them to price ids at runtime — so no environment
 * carries a mode-specific `price_...` string that silently points at the wrong
 * catalog after a mode switch.
 */

export const BILLING_PRICES = ['marquee_monthly', 'marquee_annual', 'event_pass'] as const;
export type BillingPrice = (typeof BILLING_PRICES)[number];

export function isBillingPrice(value: unknown): value is BillingPrice {
  return typeof value === 'string' && (BILLING_PRICES as readonly string[]).includes(value);
}

/** An Event Pass grants Pro for this many days from purchase. */
export const EVENT_PASS_DAYS = 30;

/**
 * How many distinct devices may vote in one presentation on the free plan.
 *
 * Counted per **run**, not per month: a presenter who runs the same weekly
 * meeting never accumulates anything, which is the failure people complain
 * about loudest in Mentimeter's 50-per-month cap. A device is counted the
 * first time it votes, so someone who opens the join screen and watches costs
 * the room nothing.
 *
 * Paid plans are uncapped — deliberately, and it is the thing to say out loud:
 * every competitor tiers the audience all the way up (Slido 1,000 on
 * Professional, Vevox 1,500, Kahoot 5,000), so "we stop counting once you
 * pay" is a claim none of them can make. See docs/GTM.md.
 */
export const FREE_AUDIENCE_CAP = 100;

/**
 * When the FloorPoll mark starts riding on public surfaces (#91).
 *
 * Set to `TERMS_EFFECTIVE_DATE`, so the mark begins the day the Terms that
 * announce it take effect. Every account meets the acceptance gate before it
 * can present, so nobody sees a marked poll without first having been shown
 * §8's vendor-mark clause — but they are shown it on the day it binds, not
 * thirty days before.
 *
 * **That is a deliberate departure from §8's notice clause, taken by the
 * product owner on 27 Aug 2026, and it is the one thing a reader of this file
 * must not mistake for an oversight.** §8 still promises to email a free
 * workspace's admins before a new limit binds them and to wait at least thirty
 * days after that email. No such email was sent, and this date does not wait.
 * The clause remains on the Terms page — `legal.test.tsx` pins it there — so
 * the page currently promises a notice period this constant does not honour.
 *
 * The earlier value was `2026-10-01`, chosen so the thirty days could run from
 * an announcement. Two lighter options were declined in favour of shipping
 * sooner: keeping that date (which needed only that the Terms reach production
 * by 1 Sep, since acceptance carries the clause — see `TERMS_VERSION`), and
 * §8's own carve-out permitting a new limit to bind workspaces created after
 * the announcement immediately, which would have cost a stamped flag at
 * presentation start and contradicted nothing.
 *
 * Applies to every workspace alike rather than only to new ones. That half is
 * unchanged and is still the right shape: making the mark depend on a
 * workspace's age is a read no public route is allowed to add.
 */
export const VENDOR_MARK_FROM = '2026-08-28T00:00:00.000Z';

/**
 * The same date, as the documents print it — the pairing `terms.ts` uses for
 * `TERMS_VERSION` and `TERMS_EFFECTIVE_DATE`. §8 of the Terms names this date,
 * and `billing.test.ts` checks the two spellings still mean the same day.
 */
export const VENDOR_MARK_DATE = '28 August 2026';

/**
 * Whether a run's public surfaces carry the FloorPoll mark.
 *
 * `!== true`, never `=== false`. `Quiz.entitledRun` is tri-state, and absent
 * means the plan could not be read — a failed billing read must never strip a
 * free workspace's branding. The audience cap reads the very same field with
 * the opposite default, for the mirror-image reason; the table on
 * `Quiz.entitledRun` is where the pair is written down.
 *
 * Compared as ISO strings, like `isProAt`: both are UTC and fixed-width, so
 * lexicographic order is chronological order and no parsing is involved.
 */
export function showsVendorMarkAt(entitledRun: boolean | undefined, at: Date): boolean {
  if (!vendorMarkLiveAt(at)) return false;
  return entitledRun !== true;
}

/**
 * Whether the notice period is over — the date half of the rule above, on its
 * own because the console asks exactly this and no more. The account page tells
 * a free workspace what is coming and then what has arrived, and deriving that
 * from `showsVendorMarkAt(false, …)` would be asking about a plan to find out
 * about a calendar.
 */
export function vendorMarkLiveAt(at: Date): boolean {
  return at.toISOString() >= VENDOR_MARK_FROM;
}

/**
 * Where Stripe sends the buyer back to, as a path in this SPA.
 *
 * Shared rather than written out in the API, because the two halves live in
 * different packages and drifted apart once already: the plan UI moved from
 * `/settings` to `/account` while the checkout redirect stayed behind, which
 * lands a buyer who has just paid on a page with no plan on it and no sign
 * anything happened. `routes.appRoutes.test.ts` asserts the router still
 * registers this path.
 */
export const BILLING_RETURN_PATH = '/account';

/**
 * What a workspace's billing state looks like to the console.
 *
 * Entitlement is deliberately a single timestamp rather than a status enum:
 * `proUntil` is derived, never written directly, from the three components
 * below, so "is this workspace Pro?" never requires a Stripe API call — only
 * a clock. A subscription keeps extending its component as invoices are
 * paid; a lapsed one simply stops there, and a platform admin can extend a
 * third component with no payment at all — see `grantUntil`.
 */
export interface WorkspaceBilling {
  /**
   * ISO timestamp until which Pro features are on. Absent means never was.
   *
   * Always the max of the three component timestamps below. The components
   * are kept separate because they age independently: a subscription
   * cancelled today must not clamp away an Event Pass bought yesterday or a
   * grant an admin put in place, and each writer recomputes only the
   * component it owns — the Stripe webhook its two, the admin grant routes
   * the third — before this one is re-derived.
   */
  proUntil?: string;
  /** Pro granted by Event Pass purchases; extended, never shortened. */
  eventPassUntil?: string;
  /**
   * Pro granted by the subscription: the end of the period Stripe has told us
   * is **paid**, plus a short grace while a renewal is still being attempted.
   *
   * Only ever raised from a paid fact — an `active`/`trialing` subscription, or
   * an `invoice.paid`. A `past_due` or `incomplete` subscription leaves it
   * where the last payment put it, which is what makes the grace a few days
   * rather than a free billing period: Stripe advances `current_period_end`
   * when it *invoices*, not when it is paid.
   */
  subscriptionUntil?: string;
  /**
   * Pro granted by a platform admin, with no payment behind it.
   *
   * A third component beside the two Stripe owns, rather than a write to
   * `proUntil` or a reuse of one of theirs. `proUntil` is re-derived on every
   * write, so a grant parked there survives only until the next Stripe
   * delivery recomputes it; and the subscription path clamps
   * `subscriptionUntil` down whenever it sees an ended status, which would
   * clamp away a grant sitting in it. Owned exclusively by the admin grant
   * routes, the way the other two are owned by their Stripe paths.
   */
  grantUntil?: string;
  /**
   * The subscription's own `current_period_end` — when it renews, or when a
   * cancelled one runs out. Distinct from `subscriptionUntil`, which carries
   * grace on top; showing a renewal date to a subscriber must use this one or
   * it reads three days late.
   */
  renewsAt?: string;
  /** Which component currently carries the entitlement. */
  source?: 'subscription' | 'event_pass' | 'grant';
  subscriptionId?: string;
  /** Stripe's subscription status verbatim (active, past_due, canceled, …). */
  subscriptionStatus?: string;
  /** True when the subscriber chose to cancel at period end. */
  cancelAtPeriodEnd?: boolean;
  /**
   * `created` of the last Stripe subscription event applied, as an ISO string.
   * Stripe does not guarantee ordering, so an event older than this one is
   * ignored rather than allowed to overwrite newer state.
   */
  lastSubscriptionEventAt?: string;
  updatedAt?: string;
}

export function isProAt(
  billing: Pick<WorkspaceBilling, 'proUntil'> | undefined,
  at: Date,
): boolean {
  if (!billing?.proUntil) return false;
  return billing.proUntil > at.toISOString();
}

/**
 * `grantUntil` for a grant that runs until an admin revokes it.
 *
 * A grant has no end date, but `proUntil` is a timestamp and every consumer
 * reads it as one, so the component is a timestamp too — parked far enough out
 * to be unreachable. The value is chosen so both existing comparisons keep
 * working untouched: ISO strings are compared lexicographically, and the '9'
 * this starts with outranks the '2' every real date does.
 *
 * A storage value, never a display one. Nothing renders it — `PlanCard` shows a
 * granted workspace no date at all, because "December 31, 9999" is the one way
 * this design can look broken.
 */
export const GRANT_FOREVER = '9999-12-31T23:59:59.999Z';

/** What a workspace's plan looks like on the operator table. */
export type WorkspacePlan = 'free' | 'marquee' | 'granted';

/**
 * The operator's verdict on a workspace: a label, not the billing record.
 *
 * `granted` is distinguished from `marquee` here and deliberately *not* on the
 * account page. An operator needs to know a comp is in place before deciding
 * whether Revoke applies; the workspace's own members are shown a plan, not a
 * note about how they came by it.
 */
export function planAt(billing: WorkspaceBilling | undefined, at: Date): WorkspacePlan {
  if (!isProAt(billing, at)) return 'free';
  return billing?.source === 'grant' ? 'granted' : 'marquee';
}

/** What a workspace's plan looks like to its own members. */
export type MemberPlan = 'free' | 'marquee' | 'event_pass';

/**
 * The plan a member is shown, from entitlement and the component carrying it.
 *
 * Deliberately not `planAt`. That function answers the operator's question and
 * returns `granted`, which must never reach a workspace's own members — see the
 * comment on it. Collapsing `grant` to `marquee` *here* means no renderer
 * downstream can leak the word, because no renderer downstream is ever handed
 * it.
 *
 * Takes `pro` rather than deriving it, because the two callers hold that answer
 * differently: the API has the stored record and a clock, and the console has
 * `BillingView.pro` — the server's own verdict — which it leads with on
 * purpose. The split mirrors `isProAt` / `planAt`.
 */
export function memberPlan(pro: boolean, source: WorkspaceBilling['source']): MemberPlan {
  if (!pro) return 'free';
  return source === 'event_pass' ? 'event_pass' : 'marquee';
}

/** `memberPlan` for a caller holding the record and a clock rather than a verdict. */
export function memberPlanAt(billing: WorkspaceBilling | undefined, at: Date): MemberPlan {
  return memberPlan(isProAt(billing, at), billing?.source);
}

/**
 * Display copy for the plan named to a member — the other half of the pair
 * `PLAN_LABELS` in WorkspaceUsageTable draws for the operator. That map keeps
 * 'Granted' apart from 'Marquee' for the same reason its own comment gives:
 * an operator needs to know a comp is in place, a member does not
 * (`memberPlan` above is where that word actually gets collapsed away).
 * PlanCard's `SUBSCRIPTIONS`/`EVENT_PASS` and `House · free` are a different
 * job again — naming things for sale and a table heading, not what a
 * workspace currently has.
 *
 * The free plan is "Free" here and "House · free" in the account page's
 * comparison table, which `docs/GTM.md` also calls House. That inconsistency
 * predates this and is deliberately left alone: reconciling it is a copy
 * decision about existing surfaces, not part of showing the plan.
 */
export const MEMBER_PLAN_LABELS: Record<MemberPlan, string> = {
  free: 'Free',
  marquee: 'Marquee',
  event_pass: 'Event Pass',
};

/**
 * Statuses in which a subscription has stopped claiming entitlement.
 *
 * One set rather than a list restated per caller. The webhook clamps
 * `subscriptionUntil` down when it sees any of these, so anything asking "is a
 * subscription standing behind this workspace?" must exclude exactly the same
 * list or it answers yes about a subscription this system has already ended.
 *
 * `past_due` is deliberately absent: it is mid-dunning, so it neither extends
 * nor revokes — the grace already bought simply runs out.
 */
export const ENDED_SUBSCRIPTION_STATUSES: ReadonlySet<string> = new Set([
  'canceled',
  'unpaid',
  'incomplete_expired',
  'paused',
]);

/**
 * Whether a subscription is still standing: on record, and not ended.
 *
 * Derived from the set above rather than restating its members, because the two
 * drifted once already — the operator table excluded only `canceled` and
 * `incomplete_expired`, so a workspace whose subscription was `unpaid` or
 * `paused` was reported as still subscribed.
 */
export function subscriptionStanding(status: string | undefined): boolean {
  return status !== undefined && !ENDED_SUBSCRIPTION_STATUSES.has(status);
}

/**
 * Statuses in which Stripe considers the current period paid for.
 *
 * The other half of the pair above, and the one that licenses a promise: only
 * these may be told a renewal date, because only these have been charged for
 * the period they are in. `incomplete` is absent — it never cleared
 * authentication — and `past_due` is absent because its last charge failed.
 */
export const PAID_SUBSCRIPTION_STATUSES: ReadonlySet<string> = new Set(['active', 'trialing']);

/** Whether Stripe has been paid for the period this subscription is in. */
export function subscriptionPaid(status: string | undefined): boolean {
  return status !== undefined && PAID_SUBSCRIPTION_STATUSES.has(status);
}
